import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import CloseIcon from '@mui/icons-material/Close';
import MarkerIcon from '@shared/marker-icon';
import Polygon from '@shared/polygon';
import Polyline from '@shared/polyline';
import { getLayerIcon, getMarkerAnchorStyle } from '@utils/icon-utils';
import { generatePath, generatePolygonPaths, getPointShapeCoords } from '@utils/map-utils';
import { isLayerMarkerVisible } from '@utils/markers-utils';
import styles from './layer-figure.scss';

const LayerFigure = ({ layer, name }) => {
  const [infoWindowPos, setInfoWindowPos] = useState(null);
  const layers = useSelector(state => state.layers);

  const {
    icon,
    map_style: mapStyle,
    style_condition: styleCondition
  } = layers[name];
  const {showEditInfoWindow} = layers[name].ui_style;
  const {editInfoWindowFields} = showEditInfoWindow ? layers[name].ui_style : {};

  const markerVisible = useSelector(state => isLayerMarkerVisible(state, name, layer));

  const closeInfoWindow = useCallback(() => setInfoWindowPos(null), []);

  const getStyle = useCallback(() => {
    const { attrs } = layer;
    if (styleCondition) {
      const style = mapStyle[attrs[styleCondition]];
      if (style) {
        return cloneDeep(style);
      }
    }
    return cloneDeep(mapStyle);
  }, [layer, mapStyle, styleCondition]);

  const computedMapStyle = useMemo(() => {
    const style = getStyle();
    if (!style) {
      return {};
    }
    return style;
  }, [getStyle]);

  const handleClick = useCallback(
    event => setInfoWindowPos(event.latLng),
    []
  );

  // AdvancedMarker's click event carries no latLng (unlike the Polygon/Polyline click above),
  // but a Point shape's marker is always at its own fixed position, so use that directly.
  const handleMarkerClick = useCallback(
    () => setInfoWindowPos(getPointShapeCoords(layer.shape)),
    [layer.shape]
  );

  useEffect(() => {
    if (!infoWindowPos || !window.mapInstance) {
      return null;
    }
    const listener = window.google.maps.event.addListener(window.mapInstance, 'click', () => {
      setInfoWindowPos(null);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [infoWindowPos]);

  if (!markerVisible) {
    return null;
  }

  const { id, shape, attrs } = layer;
  const figureId = `${name}-${id}`;
  const layerProps = {
    options: { ...computedMapStyle, clickable: Boolean(showEditInfoWindow), zIndex: 0 },
    ...(showEditInfoWindow && { onClick: handleClick })
  };

  // const closeInfoWindow = useCallback(() => setInfoWindowPos(null), []);
  
  const infoWindow = infoWindowPos && (
    <InfoWindow position={infoWindowPos} onCloseClick={closeInfoWindow}>
      <div className={styles.infoWindowContent}>
        <div className={styles.infoWindowHeader}>
          <span className={styles.infoWindowTitle}>{editInfoWindowFields.header}</span>
          <button className={styles.infoWindowClose} onClick={closeInfoWindow} type="button">
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className={styles.infoWindowBody}>
          {editInfoWindowFields.contents.map(field => {
            return (
              <div key={`${field.value}-${id}`} className={styles.row}>
                <div className={styles.label}>{field.label}</div>
                <div>{attrs[field.value]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </InfoWindow>
  );

  if (shape.type === 'Point') {
    const layerIcon = getLayerIcon(icon, 0);
    return (
      <React.Fragment key={figureId}>
        <AdvancedMarker
          onClick={showEditInfoWindow ? handleMarkerClick : null}
          position={getPointShapeCoords(shape)}
          zIndex={199}
          {...getMarkerAnchorStyle(layerIcon)}
        >
          <MarkerIcon icon={layerIcon} />
        </AdvancedMarker>
        {infoWindow}
      </React.Fragment>
    );
  }

  if (shape.type === 'LineString') {
    const path = generatePath(shape.coordinates);
    return (
      <React.Fragment key={figureId}>
        <Polyline path={path} {...layerProps} />
        {infoWindow}
      </React.Fragment>
    );
  }

  if (shape.type === 'MultiLineString') {
    return (
      <React.Fragment key={figureId}>
        {shape.coordinates.map((coords, index) => (
          <Polyline key={`${figureId}-${index}`} path={generatePath(coords)} {...layerProps} />
        ))}
        {infoWindow}
      </React.Fragment>
    );
  }

  if (shape.type === 'Polygon') {
    const polygonPath = generatePolygonPaths(shape.coordinates);
    return (
      <div key={figureId}>
        <Polygon paths={polygonPath} {...layerProps} />
        {computedMapStyle &&
          Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
          <Polyline path={generatePath(shape.coordinates[0])} {...layerProps} />
        }
        {infoWindow}
      </div>
    );
  }

  if (shape.type === 'MultiPolygon') {
    return (
      <React.Fragment key={figureId}>
        {shape.coordinates.map((coords, index) => (
          <div key={`wrapper-${figureId}-${index}`}>
            <Polygon
              key={`${figureId}-${index}`}
              paths={generatePolygonPaths(coords)}
              {...layerProps}
            />
            {computedMapStyle && Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
              <Polyline
                key={`outline-${figureId}-${index}`}
                path={generatePath(coords[0])}
                {...layerProps}
              />
            }
          </div>
        ))}
        {infoWindow}
      </React.Fragment>
    );
  }

  return null;
};

LayerFigure.propTypes = {
  layer: PropTypes.object,
  name: PropTypes.string
};

export default LayerFigure;
